import { useEffect, useState } from 'react';
import { FormIcon } from '../FormIcon.jsx';
import { SearchableSelect } from '../../components/SearchableSelect.jsx';

export function Field({ label, optional = false, hint, children, className = '' }) {
  return (
    <label className={`fm-field ${className}`}>
      <span>
        {label}
        {optional ? <em> (optional)</em> : null}
      </span>
      {children}
      {hint ? <small className="fm-hint">{hint}</small> : null}
    </label>
  );
}

export function Check({ checked, onChange, label, hint, disabled = false }) {
  return (
    <label className={`fm-check${disabled ? ' is-disabled' : ''}`}>
      <input type="checkbox" checked={Boolean(checked)} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />
      <span>
        {label}
        {hint ? <small>{hint}</small> : null}
      </span>
    </label>
  );
}

export function Toggle({ checked, onChange, label, hint, disabled = false }) {
  return (
    <label className={`fm-toggle${disabled ? ' is-disabled' : ''}`}>
      <span className="fm-toggle-text">
        <strong>{label}</strong>
        {hint ? <small>{hint}</small> : null}
      </span>
      <input type="checkbox" role="switch" checked={Boolean(checked)} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />
      <span className="fm-toggle-track" aria-hidden="true" />
    </label>
  );
}

export function ClearableSelect({ value, onChange, placeholder, options, disabled = false, ariaLabel }) {
  return (
    <div className="fm-select-wrap fm-select-clear">
      <SearchableSelect value={value ?? ''} onChange={(e) => onChange(e.target.value ? Number(e.target.value) : null)} disabled={disabled} aria-label={ariaLabel}>
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
      </SearchableSelect>
      {value ? (
        <button type="button" className="fm-clear-btn" onClick={() => onChange(null)} aria-label="Clear">
          <FormIcon name="x" size={14} />
        </button>
      ) : null}
      <FormIcon name="chevronDown" size={16} />
    </div>
  );
}

/** Comma / newline separated list input that only normalises on blur, so typing commas is not interrupted. */
export function ListInput({ value, onChange, placeholder, rows = 0 }) {
  const joined = (value || []).join(', ');
  const [text, setText] = useState(joined);
  useEffect(() => setText(joined), [joined]);
  const commit = () =>
    onChange(
      text
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean),
    );
  return rows ? (
    <textarea rows={rows} value={text} placeholder={placeholder} onChange={(e) => setText(e.target.value)} onBlur={commit} />
  ) : (
    <input value={text} placeholder={placeholder} onChange={(e) => setText(e.target.value)} onBlur={commit} />
  );
}

export function CopyField({ value, onCopied, multiline = false, label = 'Copy' }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      onCopied?.('Copied to clipboard.');
    } catch {
      onCopied?.('Copy failed — select the text and copy it manually.');
    }
  };
  return (
    <div className={`fm-copy${multiline ? ' is-multi' : ''}`}>
      {multiline ? <textarea readOnly value={value} rows={5} onFocus={(e) => e.target.select()} /> : <input readOnly value={value} onFocus={(e) => e.target.select()} />}
      <button type="button" className="fm-btn fm-btn--sm" onClick={copy}>
        <FormIcon name="copy" size={14} /> {label}
      </button>
    </div>
  );
}
